import { forwardRef } from 'react';
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
} from 'react';
import { useKaiShikiProviderValue } from '../../KaiShikiProvider';
import { resolveStyleOverride, type StyleOverride } from './styleOverrides';

export const COMMON_TOGGLE_TRACK_STYLE: CSSProperties = {
  width: 53,
  height: 24,
  border: 0,
  borderRadius: 999,
  padding: 0,
  position: 'relative',
  flex: '0 0 auto',
  appearance: 'none',
};

export const COMMON_TOGGLE_KNOB_STYLE: CSSProperties = {
  position: 'absolute',
  top: 3,
  width: 18,
  height: 18,
  borderRadius: 999,
};

const DISABLED_TRACK_STYLE: CSSProperties = {
  cursor: 'not-allowed',
  opacity: 0.67,
};

const getTrackStateStyle = (checked: boolean | undefined, colorOn: string, colorOff: string): CSSProperties => ({
  backgroundColor: checked ? colorOn : colorOff,
  cursor: 'pointer',
  transition: 'background-color 0.2s ease',
});

const getKnobStateStyle = (checked: boolean | undefined, knobColor: string): CSSProperties => ({
  left: checked ? 32 : 3,
  backgroundColor: knobColor,
  boxShadow: '0 1px 2px rgba(0, 0, 0, 0.16)',
  transition: 'left 0.2s ease',
});

export type CommonToggleKnobProps = HTMLAttributes<HTMLSpanElement> & {
  styleOverride?: StyleOverride;
};

export const CommonToggleKnob = ({ styleOverride: knobCss, style, ...props }: CommonToggleKnobProps) => (
  <span
    style={{
      ...COMMON_TOGGLE_KNOB_STYLE,
      ...style,
      ...resolveStyleOverride(knobCss),
    }}
    {...props}
  />
);

export type CommonToggleProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  trackCss?: StyleOverride;
  knobCss?: StyleOverride;
  knob?: ReactNode;
  checked?: boolean;
};

// Web-only admin primitive extracted from renovator CommonToggle without Emotion.
export const CommonToggle = forwardRef<HTMLButtonElement, CommonToggleProps>(
  (
    {
      trackCss,
      knobCss,
      knob,
      checked,
      children,
      type = 'button',
      disabled = false,
      role,
      style,
      ...props
    },
    ref,
  ) => {
    const { semantic } = useKaiShikiProviderValue();

    return (
      <button
        ref={ref}
        type={type}
        role={role ?? (checked !== undefined ? 'switch' : undefined)}
        aria-checked={checked}
        disabled={disabled}
        style={{
          ...COMMON_TOGGLE_TRACK_STYLE,
          ...getTrackStateStyle(checked, semantic.intent.success.default, semantic.border.default),
          ...(disabled ? DISABLED_TRACK_STYLE : undefined),
          ...style,
          ...resolveStyleOverride(trackCss),
        }}
        {...props}
      >
        {children ?? knob ?? (
          <CommonToggleKnob
            styleOverride={{
              ...getKnobStateStyle(checked, semantic.surface.canvas),
              ...resolveStyleOverride(knobCss),
            }}
          />
        )}
      </button>
    );
  },
);

CommonToggle.displayName = 'CommonToggle';
